<template>
  <div
    :tabindex="disabled || loading ? -1 : 0"
    :class="[
      'tw-inline-flex tw-items-center tw-justify-center tw-font-bold tw-transition-all tw-duration-200 tw-cursor-pointer focus:tw-outline-none focus:tw-ring-4 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed',
      `tw-rounded-${rounded}`,
      variantClasses,
      sizeClasses
    ]"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <!-- Icon Start -->
    <i 
      v-if="icon && iconPosition === 'start' && !loading" 
      :class="['mdi', icon, iconSizeClass, (label || $slots.default) ? (hideLabelOnMobile ? 'tw-mr-0 sm:tw-mr-2' : 'tw-mr-2') : '']"
    ></i>

    <!-- Loading Spinner -->
    <i 
      v-if="loading" 
      :class="['mdi mdi-loading mdi-spin', iconSizeClass, (label || $slots.default) ? (hideLabelOnMobile ? 'tw-mr-0 sm:tw-mr-2' : 'tw-mr-2') : '']"
    ></i>

    <!-- Label -->
    <span v-if="label" :class="{ 'tw-hidden sm:tw-inline': hideLabelOnMobile }">{{ label }}</span>
    <slot v-else></slot>

    <!-- Icon End -->
    <i 
      v-if="icon && iconPosition === 'end' && !loading" 
      :class="['mdi', icon, iconSizeClass, (label || $slots.default) ? (hideLabelOnMobile ? 'tw-ml-0 sm:tw-ml-2' : 'tw-ml-2') : '']"
    ></i>
  </div>
</template>

<script>
export default {
  name: "Button",
  props: {
    label: {
      type: String,
      default: ""
    },
    variant: {
      type: String,
      default: "primary",
      validator: (value) => {
        const valid = ["primary", "secondary", "success", "danger", "warning", "info", "dark", "outline", "ghost"];
        return value.split(' ').every(v => valid.includes(v));
      }
    },
    size: {
      type: String,
      default: "small",
      validator: (value) => ["small", "medium", "large"].includes(value)
    },
    icon: {
      type: String,
      default: ""
    },
    iconPosition: {
      type: String,
      default: "start",
      validator: (value) => ["start", "end"].includes(value)
    },
    type: {
      type: String,
      default: "button"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: String,
      default: "full"
    },
    hideLabelOnMobile: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleClick(event) {
      if (this.disabled || this.loading) return;
      
      if (this.type === 'submit') {
        const form = this.$el.closest('form');
        if (form) {
          event.preventDefault();
          // Create a native submit event and dispatch it from the form
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      }
      
      this.$emit('click', event);
    }
  },
  computed: {
    variantClasses() {
      const v = this.variant.split(' ');
      const isOutline = v.includes('outline');
      const isGhost = v.includes('ghost');
      const hasColor = v.some(val => ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark'].includes(val));
      
      const color = v.find(val => ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark'].includes(val)) || 'primary';
      const style = isOutline ? 'outline' : (isGhost ? 'ghost' : 'solid');

      // Tema neutro per compatibilità (es. variant="outline" senza specificare colori)
      if (!hasColor && (isOutline || isGhost)) {
        if (isOutline) return "tw-border tw-border-solid tw-border-slate-200 tw-bg-transparent hover:tw-bg-slate-50 tw-text-slate-700 dark:tw-border-gray-700 dark:tw-text-gray-300 dark:hover:tw-bg-gray-800 focus:tw-ring-slate-200/20";
        if (isGhost) return "tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-100 focus:tw-ring-slate-100/20 dark:tw-text-white dark:hover:tw-bg-gray-800/50";
      }

      const configs = {
        primary: {
          solid: "tw-bg-primary-500 tw-text-white hover:tw-bg-primary-600 focus:tw-ring-primary-500/20",
          outline: "tw-border tw-border-solid tw-border-primary-500 tw-text-primary-500 hover:tw-bg-primary-100 dark:tw-border-white dark:tw-text-white dark:hover:tw-bg-white/20 focus:tw-ring-primary-500/10",
          ghost: "tw-text-primary-500 hover:tw-bg-primary-50 focus:tw-ring-primary-500/10"
        },
        secondary: {
          solid: "tw-bg-secondary-500 tw-text-white hover:tw-bg-secondary-600 focus:tw-ring-secondary-500/20",
          outline: "tw-border tw-border-solid tw-border-secondary-500 tw-text-secondary-500 hover:tw-bg-secondary-50 focus:tw-ring-secondary-500/10",
          ghost: "tw-text-secondary-500 hover:tw-bg-secondary-50 focus:tw-ring-secondary-500/10"
        },
        success: {
          solid: "tw-bg-success tw-text-white hover:tw-opacity-90 focus:tw-ring-success/20",
          outline: "tw-border tw-border-solid tw-border-success tw-text-success hover:tw-bg-success/5 focus:tw-ring-success/10",
          ghost: "tw-text-success hover:tw-bg-success/5 focus:tw-ring-success/10"
        },
        danger: {
          solid: "tw-bg-danger tw-text-white hover:tw-opacity-90 focus:tw-ring-danger/20",
          outline: "tw-border tw-border-solid tw-border-danger tw-text-danger hover:tw-bg-danger/5 focus:tw-ring-danger/10",
          ghost: "tw-text-danger hover:tw-bg-danger/5 focus:tw-ring-danger/10"
        },
        warning: {
          solid: "tw-bg-warning tw-text-white hover:tw-opacity-90 focus:tw-ring-warning/20",
          outline: "tw-border tw-border-solid tw-border-warning tw-text-warning hover:tw-bg-warning/5 focus:tw-ring-warning/10",
          ghost: "tw-text-warning hover:tw-bg-warning/5 focus:tw-ring-warning/10"
        },
        info: {
          solid: "tw-bg-info tw-text-white hover:tw-opacity-90 focus:tw-ring-info/20",
          outline: "tw-border tw-border-solid tw-border-info tw-text-info hover:tw-bg-info/5 focus:tw-ring-info/10",
          ghost: "tw-text-info hover:tw-bg-info/5 focus:tw-ring-info/10"
        },
        dark: {
          solid: "tw-bg-dark tw-text-white hover:tw-opacity-90 focus:tw-ring-dark/20",
          outline: "tw-border tw-border-solid tw-border-dark tw-text-dark hover:tw-bg-dark/5 focus:tw-ring-dark/10",
          ghost: "tw-text-dark hover:tw-bg-dark/5 focus:tw-ring-dark/10"
        }
      };

      return configs[color][style];
    },
    sizeClasses() {
      if (this.hideLabelOnMobile) {
        const responsiveSizes = {
          small: "tw-px-2 sm:tw-px-4 tw-py-1.5 tw-text-xs",
          medium: "tw-px-3 sm:tw-px-6 tw-py-2.5 tw-text-sm",
          large: "tw-px-4 sm:tw-px-8 tw-py-3.5 tw-text-base"
        };
        return responsiveSizes[this.size] || responsiveSizes.medium;
      }

      if (!this.label && (this.icon || this.loading)) {
        const iconOnlySizes = {
          small: "tw-w-8 tw-h-8 tw-text-xs",
          medium: "tw-w-10 tw-h-10 tw-text-sm",
          large: "tw-w-12 tw-h-12 tw-text-base"
        };
        return iconOnlySizes[this.size] || iconOnlySizes.medium;
      }

      const sizes = {
        small: "tw-px-4 tw-py-1.5 tw-text-xs",
        medium: "tw-px-6 tw-py-2.5 tw-text-sm",
        large: "tw-px-8 tw-py-3.5 tw-text-base"
      };
      return sizes[this.size] || sizes.medium;
    },
    iconSizeClass() {
      const iconSizes = {
        small: "tw-text-base",
        medium: "tw-text-lg",
        large: "tw-text-xl"
      };
      return iconSizes[this.size] || iconSizes.medium;
    }
  }
};
</script>

<style scoped>
</style>
